import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { useAuth } from "#auth.tsx";
import { useLanguage } from "#i18n.tsx";
import { loginDestination } from "#login-destination.ts";

/** Staff login backed by the existing refresh-cookie flow. */
export function LoginPage() {
  const { api, state, login, loginWithPasskey, loginWithOneTimeToken } =
    useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const destination = loginDestination(searchParams.get("returnTo"));
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const deviceSignInStarted = useRef(false);

  useEffect(() => {
    if (
      deviceSignInStarted.current ||
      !api.supportsPasskeys ||
      state.kind !== "anonymous"
    )
      return;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const token = fragment.get("one-time-token");
    if (!token) return;
    deviceSignInStarted.current = true;
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
    setSubmitting(true);
    void loginWithOneTimeToken(token).then((result) => {
      if (result.ok) navigate(destination, { replace: true });
      else {
        setError(result.error.message);
        setSubmitting(false);
      }
    });
  }, [
    api.supportsPasskeys,
    destination,
    loginWithOneTimeToken,
    navigate,
    state.kind,
  ]);

  if (state.kind === "authenticated")
    return <Navigate to={destination} replace />;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);
    const fields = new FormData(event.currentTarget);
    const result = await login(
      String(fields.get("username") ?? ""),
      String(fields.get("password") ?? ""),
    );
    if (result.ok) {
      navigate(destination, { replace: true });
    } else {
      setError(result.error.message);
    }
    setSubmitting(false);
  };

  const usePasskey = async () => {
    setError(undefined);
    setSubmitting(true);
    const result = await loginWithPasskey();
    if (result.ok) navigate(destination, { replace: true });
    else setError(result.error.message);
    setSubmitting(false);
  };

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={(event) => void submit(event)}>
        <img
          className="brand-logo large"
          src="/title-icon.png"
          alt="Pocket Rice"
        />
        <p className="eyebrow">{t("login.eyebrow")}</p>
        <h1>{t("login.title")}</h1>
        <p className="muted">{t("login.description")}</p>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <label>
          {t("login.username")}
          <input
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            required
          />
        </label>
        <label>
          {t("login.password")}
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </label>
        <button className="primary" disabled={submitting} type="submit">
          {submitting ? t("login.signingIn") : t("login.signIn")}
        </button>
        {api.supportsPasskeys && (
          <button
            className="secondary"
            disabled={submitting}
            type="button"
            onClick={() => void usePasskey()}
          >
            {t("login.passkey")}
          </button>
        )}
      </form>
    </main>
  );
}
